import React from 'react';
import Icon from '../../../components/AppIcon';

const PriceComparisonPanel = ({ originalPricing, updatedPricing, changeFees }) => {
  const priceDifference = (updatedPricing?.total || 0) - (originalPricing?.total || 0);
  const isIncrease = priceDifference > 0;
  const isDecrease = priceDifference < 0;

  const formatCurrency = (amount) => {
    return `₱${amount?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden shadow-lg sticky top-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-accent to-primary p-4">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Icon name="DollarSign" size={20} />
          Price Comparison
        </h2>
      </div>

      <div className="p-4 space-y-4">
        {/* Original Pricing */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Original Pricing
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Base Amount</span>
              <span className="text-sm font-medium text-foreground">
                {formatCurrency(originalPricing?.baseAmount)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Taxes & Fees</span>
              <span className="text-sm font-medium text-foreground">
                {formatCurrency(originalPricing?.taxesAndFees)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Service Fee</span>
              <span className="text-sm font-medium text-foreground">
                {formatCurrency(originalPricing?.serviceFee)}
              </span>
            </div>
            <div className="pt-2 border-t border-border flex items-center justify-between">
              <span className="text-base font-bold text-foreground">Original Total</span>
              <span className="text-lg font-bold text-muted-foreground">
                {formatCurrency(originalPricing?.total)}
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-card px-3 text-xs text-muted-foreground uppercase">
              Updated
            </span>
          </div>
        </div>

        {/* Updated Pricing */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-primary uppercase tracking-wide">
            Updated Pricing
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Base Amount</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                  {formatCurrency(updatedPricing?.baseAmount)}
                </span>
                {updatedPricing?.baseAmount !== originalPricing?.baseAmount && (
                  <Icon
                    name={updatedPricing?.baseAmount > originalPricing?.baseAmount ? 'TrendingUp' : 'TrendingDown'}
                    size={14}
                    className={updatedPricing?.baseAmount > originalPricing?.baseAmount ? 'text-error' : 'text-success'}
                  />
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Taxes & Fees</span>
              <span className="text-sm font-medium text-foreground">
                {formatCurrency(updatedPricing?.taxesAndFees)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Service Fee</span>
              <span className="text-sm font-medium text-foreground">
                {formatCurrency(updatedPricing?.serviceFee)}
              </span>
            </div>
            {changeFees > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-warning">Modification Fees</span>
                <span className="text-sm font-medium text-warning">
                  {formatCurrency(changeFees)}
                </span>
              </div>
            )}
            <div className="pt-2 border-t border-border flex items-center justify-between">
              <span className="text-base font-bold text-foreground">New Total</span>
              <span className="text-lg font-bold text-primary">
                {formatCurrency(updatedPricing?.total)}
              </span>
            </div>
          </div>
        </div>

        {/* Price Difference Indicator */}
        {priceDifference !== 0 && (
          <div className={`p-4 rounded-lg border ${
            isIncrease
              ? 'bg-error/10 border-error/20' :'bg-success/10 border-success/20'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon
                  name={isIncrease ? 'ArrowUp' : 'ArrowDown'}
                  size={20}
                  className={isIncrease ? 'text-error' : 'text-success'}
                />
                <span className={`text-sm font-semibold ${
                  isIncrease ? 'text-error' : 'text-success'
                }`}>
                  {isIncrease ? 'Price Increase' : 'Price Decrease'}
                </span>
              </div>
              <span className={`text-lg font-bold ${
                isIncrease ? 'text-error' : 'text-success'
              }`}>
                {isIncrease ? '+' : ''}{formatCurrency(Math.abs(priceDifference))}
              </span>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="p-3 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">
                All prices include applicable taxes and fees. Modification fees are non-refundable.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceComparisonPanel;